import React from 'react';
import { Box, Typography } from '@mui/material';

/**
 * Generic right-panel preview card.
 *
 * Props:
 *  icon        – JSX element rendered inside the icon bubble
 *  iconBg      – background colour of the icon bubble
 *  iconRound   – true → circle bubble, false → rounded-square bubble
 *  border      – optional CSS border string (e.g. '2px solid #F15A24')
 *  background  – card background (default '#fff'; use 'transparent' for stat layout)
 *  preTitle    – small label above the stat (e.g. 'While you wait...')
 *  stat        – large highlighted number/string (e.g. '88%')
 *  statColor   – colour of the stat (default '#E05C35')
 *  title       – main heading
 *  subtitle    – body text below the heading
 *  items       – array of { icon, bg, textColor, bold, text }
 *  footnote    – closing line under the list (slide 6's "Nothing is locked in")
 */
const ProgramPreviewCard = ({
    icon, iconBg, iconRound = false,
    border, background = 'var(--brand-surface, #fff)',
    preTitle, stat, statColor = '#FF6900',
    title, subtitle,
    items = [], footnote,
    paragraphs = [], bullets = [],
}) => {
    return (
        <Box sx={{
            fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
            borderRadius: '16px',
            padding: '40px 32px',
            maxWidth: '480px',
            width: '100%',
            background,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '12px',
        }}>
            {icon && (
                <Box sx={{
                    width: '56px',
                    height: '56px',
                    borderRadius: iconRound ? '50%' : '12px',
                    background: iconBg || '#f0f0f0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: '4px',
                    flexShrink: 0,
                }}>
                    {icon}
                </Box>
            )}

            {preTitle && (
                <Typography sx={{ fontSize: '15px', fontWeight: 700, color: 'var(--brand-text, #1a1a1a)', textAlign: 'center' }}>
                    {preTitle}
                </Typography>
            )}

            {stat && (
                <Typography sx={{ fontSize: '80px', fontWeight: 800, color: statColor, textAlign: 'center', lineHeight: 1 }}>
                    {stat}
                </Typography>
            )}

            {title && (
                <Typography sx={{ fontSize: stat ? '24px' : '20px', fontWeight: 700, color: 'var(--brand-text, #1a1a1a)', textAlign: 'center', lineHeight: 1.3 }}>
                    {title}
                </Typography>
            )}

            {subtitle && (
                <Typography sx={{ fontSize: '15px', color: 'var(--brand-text-muted, #666)', textAlign: 'center', lineHeight: 1.6, mb: '4px' }}>
                    {subtitle}
                </Typography>
            )}

            {/*
             * Slide 6 renders this card as a compact LEFT-ALIGNED text block:
             * two short paragraphs, then plain bullets. The boxed-icon rows
             * below (items) are the older treatment — they are roughly three
             * times the height, which pushed the card past the fold and put a
             * scrollbar in a panel the slide shows whole.
             */}
            {paragraphs.length > 0 && (
                <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {paragraphs.map((para, i) => (
                        <Typography key={i} sx={{
                            fontSize: '13.5px',
                            color: 'var(--brand-text-muted, #666)',
                            textAlign: 'left',
                            lineHeight: 1.55,
                        }}>
                            {para}
                        </Typography>
                    ))}
                </Box>
            )}

            {bullets.length > 0 && (
                <Box component="ul" sx={{
                    width: '100%', margin: '2px 0 0', paddingLeft: '20px',
                    display: 'flex', flexDirection: 'column', gap: '6px',
                }}>
                    {bullets.map((b, i) => (
                        <Typography key={i} component="li" sx={{
                            fontSize: '13.5px',
                            color: 'var(--brand-text, #333)',
                            textAlign: 'left',
                            lineHeight: 1.5,
                        }}>
                            <strong>{b.bold}</strong>{b.text}
                        </Typography>
                    ))}
                </Box>
            )}

            {items.length > 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
                    {items.map((item, i) => (
                        <Box key={i} sx={{
                            background: item.bg || 'var(--brand-surface, #F7F7F8)',
                            borderRadius: '10px',
                            padding: '14px 16px',
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '12px',
                        }}>
                            {item.icon}
                            <Typography sx={{ fontSize: '14px', color: item.textColor || 'var(--brand-text, #333)', lineHeight: 1.5 }}>
                                <strong>{item.bold}</strong>{item.text}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            )}

            {footnote && (
                <Typography sx={{
                    fontSize: '13.5px',
                    color: 'var(--brand-text-muted, #666)',
                    // Slide 6 sets this line flush left with the bullets above it.
                    textAlign: 'left',
                    width: '100%',
                    lineHeight: 1.6,
                    mt: '4px',
                }}>
                    {footnote}
                </Typography>
            )}
        </Box>
    );
};

export default ProgramPreviewCard;
